/**
 * @file Frontend API client — materialAnalysisService.
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";

/** Type definition — Material Analysis Params. */
export interface MaterialAnalysisParams {
  materialId: number;
  subMaterialId?: number;
  propertyMin?: number;
  propertyMax?: number;
  propertyKey?: string;
  applyDataSpecificity?: boolean;
  startYear?: number;
  endYear?: number;
  geographicScope?: string;
  country?: string;
  state?: string;
  technologyId?: number;
  manufacturerId?: number;
}

/** Type definition — Submission Summary. */
export interface SubmissionSummary {
  embodiedEnergyValue?: number;
  embodiedEnergyUnit?: string;
  embodiedCarbonValue: number;
  embodiedCarbonUnit: string;
}

/** Type definition — Material Analysis Submission. */
export interface MaterialAnalysisSubmission {
  submissionId: string;
  submissionCode: string;
  materialId: number;
  materialName: string;
  materialCode: string;
  subMaterialId: number | null;
  subMaterialName: string | null;
  a1: boolean;
  a2: boolean;
  a3: boolean;
  a1_a3: boolean;
  dataReview?: string;
  dataCompleteness?: string;
  dataAcquisition?: string;
  startYear?: number | string;
  endYear?: number | string;
  submittedDate?: string | null;
  submitted_date?: string | null;
  createdAt?: string | null;
  a1Summary?: SubmissionSummary | null;
  a2Summary?: SubmissionSummary | null;
  a3Summary?: SubmissionSummary | null;
  a1a3Summary?: SubmissionSummary | null;
  temporalSpecificity?: Record<string, unknown>;
  geographicalSpecificity?: Record<string, unknown>;
  technologicalSpecificity?: { id: number; name: string; description?: string | null };
  manufacturerSpecificity?: { id: number; name: string; description?: string | null };
  /** Nested background data (legacy / fallback) */
  background?: {
    startYear?: number | string;
    endYear?: number | string;
    collectionStartYear?: number | string;
    collectionEndYear?: number | string;
    dataCompleteness?: string;
    dataAcquisition?: string;
    technologyId?: number | null;
    manufacturerId?: number | null;
    geoRegion?: string;
    geographicalCoverage?: string;
    geographicScope?: string;
    country?: string;
    productionPlantState?: string;
  } | null;
}

/** Exported function — format Material Analysis Submitted On. */
export function formatMaterialAnalysisSubmittedOn(s: MaterialAnalysisSubmission): string {
  const raw = s.submittedDate ?? s.submitted_date;
  if (raw != null && raw !== "") {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
  }
  const c = s.createdAt;
  if (c != null && c !== "") {
    const d = new Date(c);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
  }
  return "—";
}

/** Type definition — Embodied Carbon Factor Stats. */
export interface EmbodiedCarbonFactorStats {
  unit: string;
  sampleSize: number;
  average: number;
  min: number;
  max: number;
  standardDeviation: number;
  redDotValue: number | null;
  method: "UPPER_95_CI" | "MAX_FOR_SMALL_SAMPLE";
}

/** Type definition — Material Analysis Pool. */
export interface MaterialAnalysisPool {
  count: number;
  data: MaterialAnalysisSubmission[];
  embodiedCarbonFactor: EmbodiedCarbonFactorStats;
}

/** Type definition — Material Analysis Response. */
export interface MaterialAnalysisResponse {
  success: boolean;
  applyDataSpecificity: boolean;
  materials: MaterialAnalysisPool;
  reliable: MaterialAnalysisPool;
  dataSpecificity: MaterialAnalysisPool;
}

/** Type definition — Sub Material Option. */
export interface SubMaterialOption {
  id: number;
  name: string;
}

/** Sub-types that appear in at least one submission for the selected material. */
export function filterSubMaterialsWithAnalysisData(
  submissions: MaterialAnalysisSubmission[],
  catalogSubMaterials: SubMaterialOption[]
): SubMaterialOption[] {
  if (!catalogSubMaterials.length || !submissions.length) return [];

  const idsWithData = new Set<number>();
  const namesWithData = new Set<string>();

  for (const row of submissions) {
    const id = row.subMaterialId;
    if (id != null && Number.isFinite(Number(id))) {
      idsWithData.add(Number(id));
    }
    const name = String(row.subMaterialName ?? "").trim().toLowerCase();
    if (name) namesWithData.add(name);
  }

  return catalogSubMaterials.filter(
    (sm) =>
      idsWithData.has(sm.id) ||
      namesWithData.has(sm.name.trim().toLowerCase())
  );
}

// ------------------------------------
// GET MATERIAL ANALYSIS DATA
// ------------------------------------
const normalizeMaterialAnalysisResponse = (res: any): MaterialAnalysisResponse => {
  const body = res?.data ?? res;
  if (!body) {
    return {
      success: false,
      applyDataSpecificity: false,
      materials: { count: 0, data: [], embodiedCarbonFactor: createEmptyEcf() },
      reliable: { count: 0, data: [], embodiedCarbonFactor: createEmptyEcf() },
      dataSpecificity: { count: 0, data: [], embodiedCarbonFactor: createEmptyEcf() },
    };
  }

  const createPool = (raw: any): MaterialAnalysisPool => {
    const data = Array.isArray(raw?.data) ? raw.data : [];
    const count = typeof raw?.count === "number" ? raw.count : data.length;
    const ecf = raw?.embodiedCarbonFactor;
    return {
      count,
      data,
      embodiedCarbonFactor: ecf
        ? {
            unit: ecf.unit ?? "CO₂eq/kg",
            sampleSize: ecf.sampleSize ?? 0,
            average: ecf.average ?? 0,
            min: ecf.min ?? 0,
            max: ecf.max ?? 0,
            standardDeviation: ecf.standardDeviation ?? 0,
            redDotValue: ecf.redDotValue ?? null,
            method: ecf.method ?? "MAX_FOR_SMALL_SAMPLE",
          }
        : createEmptyEcf(),
    };
  };

  return {
    success: body.success ?? true,
    applyDataSpecificity: body.applyDataSpecificity ?? false,
    materials: createPool(body.materials ?? {}),
    reliable: createPool(body.reliable ?? {}),
    dataSpecificity: createPool(body.dataSpecificity ?? {}),
  };
};

function createEmptyEcf(): EmbodiedCarbonFactorStats {
  return {
    unit: "CO₂eq/kg",
    sampleSize: 0,
    average: 0,
    min: 0,
    max: 0,
    standardDeviation: 0,
    redDotValue: null,
    method: "MAX_FOR_SMALL_SAMPLE",
  };
}

/** Exported const — get Material Analysis. */
export const getMaterialAnalysis = async (
  params: MaterialAnalysisParams
): Promise<{ data: MaterialAnalysisResponse }> => {
  // Backend applies reliability rules server-side; do not add dataReview /
  // dataCompleteness / dataAcquisition as query params (removed from API).
  const res = await axiosClient.get(API_URLS.USER_DASHBOARD.MATERIAL_ANALYSIS, {
    params: { ...params },
  });
  return { data: normalizeMaterialAnalysisResponse(res) };
};
